import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CTAButton, CTAGroup } from '../components/CTAButtons';
import { AnimatedVideo } from '../components/AnimatedVideo';
import { Testimonials } from '../components/Testimonials';
import { TrustBadges } from '../components/TrustBadges';
import { FAQ } from '../components/FAQ';
import { CheckIcon, ShieldIcon, LockIcon, ArrowRightIcon, TrendingUpIcon, MoonIcon, SparklesIcon, BanIcon, ShieldCheckIcon, HeartHandshakeIcon, RocketIcon, HelpCircleIcon } from 'lucide-react';
export const Home: React.FC = () => {
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  const staggerChildren = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  const transformBenefits = [{
    title: 'No More Sleepless Nights',
    description: 'Take away the stress of these debts hanging over you by transferring your company — not your business — to us.',
    image: 'https://images.unsplash.com/photo-1541781774459-bb2af2f05b55?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    icon: MoonIcon
  }, {
    title: "Get Back to Doing What You're Good At",
    description: "Worrying about outstanding bills can restrict your creative side. By transferring the company to us, you'll be free to build the dream again.",
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    icon: SparklesIcon
  }, {
    title: 'No Bills or Bailiffs',
    description: 'Once the company is transferred to us, all the bills will stop. They will no longer be your bills but ours — and the bailiffs will stop.',
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    icon: BanIcon
  }, {
    title: 'No Bankruptcy or Insolvency',
    description: 'We take care of all the issues from dealing with creditors to handling any bankruptcy. As you no longer own the company, your name and reputation will be left intact.',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    icon: ShieldCheckIcon
  }, {
    title: 'Ongoing Support',
    description: 'We will be available for support as you transform from your old indebted company to your new opportunity — whatever that may be.',
    image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    icon: HeartHandshakeIcon
  }, {
    title: 'Imagine...',
    description: 'All that knowledge and experience with no debt — free to start again, try something new without the past hanging over your head, or put your feet up knowing you escaped that bad experience.',
    image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    icon: RocketIcon
  }];
  const pricingPackages = [{
    title: 'Basic Business Exit',
    price: 'POA',
    features: ['Simple company transfer', 'Limited debts (under £50k)', 'No major creditor issues', 'Standard documentation', '30-day completion target']
  }, {
    title: 'Urgent Rescue',
    price: 'POA',
    recommended: true,
    features: ['Expedited company transfer', 'Medium debts (£50k-£150k)', 'Some creditor arrears', 'Priority documentation', '14-day completion target']
  }, {
    title: 'Complex Rescue',
    price: 'POA',
    features: ['Comprehensive transfer', 'Significant debts (£150k+)', 'Substantial creditor issues', 'Complex documentation', 'Tailored completion timeline']
  }];
  return <div className="bg-white" data-id="element-335">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-black via-gray-900 to-black text-white" data-id="element-336">
        <div className="absolute inset-0 opacity-25 bg-[url('https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&w=1500&q=80')] bg-cover bg-center" data-id="element-337"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" data-id="element-338"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32" data-id="element-339">
          <motion.div className="max-w-3xl" initial="hidden" animate="visible" variants={fadeIn} data-id="element-340">
            <div className="inline-flex items-center bg-red-600/20 border border-red-600/30 rounded-full px-4 py-2 mb-6" data-id="element-341">
              <TrendingUpIcon className="h-4 w-4 mr-2 text-red-400" data-id="element-342" />
              <span className="text-sm text-red-200" data-id="element-343">
                Limited availability - Contact us today
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6" data-id="element-344">
              Struggling With Business Debt? We Help You Hand It Over Safely.
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-300" data-id="element-345">
              We match distressed Ltd companies with buyers willing to take over
              debts, liabilities, and challenges so you can move forward.
            </p>
            <CTAGroup className="mb-8" showPriority={true} data-id="element-346" />
            <div className="flex items-center text-sm text-gray-300" data-id="element-347">
              <LockIcon className="h-4 w-4 mr-2" data-id="element-348" />
              <span data-id="element-349">Completely confidential, discreet service</span>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Trust Badges */}
      <section className="py-12 bg-gray-50 border-b border-gray-200" data-id="element-350">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-351">
          <TrustBadges data-id="element-352" />
        </div>
      </section>

      {/* Transform Your Business Section */}
      <section className="py-16 md:py-24 bg-white" data-id="element-353">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-354">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-355">
            <h2 className="text-3xl md:text-5xl font-bold mb-4" data-id="element-356">
              Transform Your Business <span className="text-red-600" data-id="element-357">&</span>{' '}
              Your Life Today
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-358">
              Transfer your company to us and leave the debt behind. Here's what
              changes for you:
            </p>
          </motion.div>

          <motion.div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerChildren} data-id="element-359">
            {transformBenefits.map((benefit, index) => <motion.div key={index} className="relative h-80 rounded-xl overflow-hidden group" variants={fadeIn} data-id="element-360">
                {/* Background Image */}
                <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110" style={{
              backgroundImage: `url(${benefit.image})`
            }} data-id="element-361" />
                {/* Dark Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/70 to-black/40" data-id="element-362" />
                {/* Content */}
                <div className="relative h-full flex flex-col justify-end p-6 text-white" data-id="element-363">
                  <div className="mb-3" data-id="element-364">
                    <benefit.icon className="h-8 w-8 text-red-500" data-id="element-365" />
                  </div>
                  <h3 className="text-xl font-bold mb-2" data-id="element-366">{benefit.title}</h3>
                  <p className="text-gray-200 text-sm leading-relaxed" data-id="element-367">
                    {benefit.description}
                  </p>
                </div>
              </motion.div>)}
          </motion.div>

          <motion.div className="text-center mt-12" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-368">
            <CTAGroup className="justify-center" showPriority={true} data-id="element-369" />
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-white" data-id="element-370">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-371">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-372">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-373">
              How It Works
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-374">
              Our process is straightforward and designed to help you exit your
              troubled business quickly and efficiently.
            </p>
          </motion.div>
          <motion.div className="grid md:grid-cols-3 gap-8" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerChildren} data-id="element-375">
            <motion.div className="bg-gray-50 p-8 rounded-lg text-center" variants={fadeIn} data-id="element-376">
              <div className="w-12 h-12 bg-red-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-6" data-id="element-377">
                1
              </div>
              <h3 className="text-xl font-bold mb-4" data-id="element-378">Consultation</h3>
              <p className="text-gray-600" data-id="element-379">
                We assess your company's situation, including debts,
                liabilities, and assets to determine the best approach.
              </p>
            </motion.div>
            <motion.div className="bg-gray-50 p-8 rounded-lg text-center" variants={fadeIn} data-id="element-380">
              <div className="w-12 h-12 bg-red-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-6" data-id="element-381">
                2
              </div>
              <h3 className="text-xl font-bold mb-4" data-id="element-382">Buyer Matching</h3>
              <p className="text-gray-600" data-id="element-383">
                We source a qualified buyer willing to take over your company
                with its existing liabilities and challenges.
              </p>
            </motion.div>
            <motion.div className="bg-gray-50 p-8 rounded-lg text-center" variants={fadeIn} data-id="element-384">
              <div className="w-12 h-12 bg-red-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-6" data-id="element-385">
                3
              </div>
              <h3 className="text-xl font-bold mb-4" data-id="element-386">Transfer & Exit</h3>
              <p className="text-gray-600" data-id="element-387">
                We facilitate the legal transfer of your company, allowing you
                to walk away from the business and its debts.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Who We Help */}
      <section className="py-16 md:py-24 bg-gray-50" data-id="element-388">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-389">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-390">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-391">Who We Help</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-392">
              We specialise in helping Ltd company owners facing these
              challenging situations:
            </p>
          </motion.div>
          <motion.div className="grid md:grid-cols-2 gap-8" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerChildren} data-id="element-393">
            <motion.div className="bg-white p-6 rounded-lg shadow-sm flex" variants={fadeIn} data-id="element-394">
              <div className="mr-4" data-id="element-395">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center" data-id="element-396">
                  <CheckIcon className="h-5 w-5 text-red-600" data-id="element-397" />
                </div>
              </div>
              <div data-id="element-398">
                <h3 className="text-lg font-bold mb-2" data-id="element-399">
                  Mounting Business Debt
                </h3>
                <p className="text-gray-600" data-id="element-400">
                  Companies struggling with suppliers, creditors, and mounting
                  financial obligations that are becoming unmanageable.
                </p>
              </div>
            </motion.div>
            <motion.div className="bg-white p-6 rounded-lg shadow-sm flex" variants={fadeIn} data-id="element-401">
              <div className="mr-4" data-id="element-402">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center" data-id="element-403">
                  <CheckIcon className="h-5 w-5 text-red-600" data-id="element-404" />
                </div>
              </div>
              <div data-id="element-405">
                <h3 className="text-lg font-bold mb-2" data-id="element-406">
                  Loans, Utilities & Unsecured Debt
                </h3>
                <p className="text-gray-600" data-id="element-407">
                  Businesses burdened with business loans, utility arrears, and
                  unsecured debt that's creating cash flow problems and mounting
                  pressure from creditors.
                </p>
              </div>
            </motion.div>
            <motion.div className="bg-white p-6 rounded-lg shadow-sm flex" variants={fadeIn} data-id="element-408">
              <div className="mr-4" data-id="element-409">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center" data-id="element-410">
                  <CheckIcon className="h-5 w-5 text-red-600" data-id="element-411" />
                </div>
              </div>
              <div data-id="element-412">
                <h3 className="text-lg font-bold mb-2" data-id="element-413">Lease Commitments</h3>
                <p className="text-gray-600" data-id="element-414">
                  Companies tied into expensive property or equipment leases
                  that are draining cash flow and creating ongoing liabilities.
                </p>
              </div>
            </motion.div>
            <motion.div className="bg-white p-6 rounded-lg shadow-sm flex" variants={fadeIn} data-id="element-415">
              <div className="mr-4" data-id="element-416">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center" data-id="element-417">
                  <CheckIcon className="h-5 w-5 text-red-600" data-id="element-418" />
                </div>
              </div>
              <div data-id="element-419">
                <h3 className="text-lg font-bold mb-2" data-id="element-420">Insolvency Risk</h3>
                <p className="text-gray-600" data-id="element-421">
                  Businesses at risk of insolvency proceedings who need a more
                  controlled exit strategy before formal proceedings begin.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Testimonials */}
      <section className="py-16 md:py-24 bg-white" data-id="element-422">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-423">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-424">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-425">
              Success Stories
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-426">
              Real business owners who found their way forward with our help
            </p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-427">
            <Testimonials data-id="element-428" />
          </motion.div>
        </div>
      </section>

      {/* FAQ Preview Section */}
      <section className="py-16 md:py-24 bg-gray-50" data-id="element-429">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-430">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-431">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-6" data-id="element-432">
              <HelpCircleIcon className="h-8 w-8 text-red-600" data-id="element-433" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-434">
              Common Questions
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-435">
              Quick answers to the questions we hear most often
            </p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-436">
            <FAQ limit={4} data-id="element-437" />
          </motion.div>
          <motion.div className="text-center mt-12" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-438">
            <Link to="/faq" data-id="element-439">
              <button className="inline-flex items-center px-6 py-3 bg-red-700 text-white rounded-md hover:bg-red-800 transition-colors" data-id="element-440">
                <span data-id="element-441">View All FAQs & Other Services</span>
                <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-442" />
              </button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Video Explainer */}
      <section className="py-16 md:py-24 bg-gray-50" data-id="element-443">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-444">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-445">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-446">
              See How It Works
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-447">
              Watch our 30-second explainer to understand how we can help you
              move forward from business debt.
            </p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-448">
            <AnimatedVideo data-id="element-449" />
          </motion.div>
        </div>
      </section>
      {/* Pricing At A Glance */}
      <section className="py-16 md:py-24 bg-white" data-id="element-450">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-451">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-452">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-453">
              Pricing At A Glance
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-454">
              Transparent pricing based on the complexity of your situation
            </p>
          </motion.div>
          <motion.div className="grid md:grid-cols-3 gap-8" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerChildren} data-id="element-455">
            {pricingPackages.map((pkg, index) => <motion.div key={index} className={`border rounded-lg overflow-hidden ${pkg.recommended ? 'border-red-600 shadow-lg' : 'border-gray-200'}`} variants={fadeIn} data-id="element-456">
                {pkg.recommended && <div className="bg-red-600 text-white text-center py-1 text-sm font-medium" data-id="element-457">
                    Recommended
                  </div>}
                <div className="p-6" data-id="element-458">
                  <h3 className="text-xl font-bold mb-2" data-id="element-459">{pkg.title}</h3>
                  <p className="text-3xl font-bold mb-4" data-id="element-460">{pkg.price}</p>
                  <ul className="space-y-3 mb-6" data-id="element-461">
                    {pkg.features.map((feature, idx) => <li key={idx} className="flex items-start" data-id="element-462">
                        <CheckIcon className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-463" />
                        <span className="text-gray-700" data-id="element-464">{feature}</span>
                      </li>)}
                  </ul>
                  <p className="text-xs text-gray-500 mb-4" data-id="element-465">
                    Contact us for a personalised quote
                  </p>
                </div>
              </motion.div>)}
          </motion.div>
          <motion.div className="text-center mt-12" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-466">
            <Link to="/services" data-id="element-467">
              <button className="inline-flex items-center px-6 py-3 bg-red-700 text-white rounded-md hover:bg-red-800 transition-colors" data-id="element-468">
                <span data-id="element-469">View Full Pricing Details</span>
                <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-470" />
              </button>
            </Link>
          </motion.div>
        </div>
      </section>
      {/* Trust Statements */}
      <section className="py-16 md:py-24 bg-black text-white" data-id="element-471">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-472">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-473">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-474">
              Our Commitment
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto" data-id="element-475">
              We understand the sensitive nature of business financial
              difficulties.
            </p>
          </motion.div>
          <motion.div className="grid md:grid-cols-3 gap-8" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerChildren} data-id="element-476">
            <motion.div className="bg-gray-900 p-8 rounded-lg" variants={fadeIn} data-id="element-477">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mb-6" data-id="element-478">
                <LockIcon className="h-6 w-6" data-id="element-479" />
              </div>
              <h3 className="text-xl font-bold mb-4" data-id="element-480">Confidentiality</h3>
              <p className="text-gray-300" data-id="element-481">
                All discussions and information shared are treated with absolute
                confidentiality and discretion.
              </p>
            </motion.div>
            <motion.div className="bg-gray-900 p-8 rounded-lg" variants={fadeIn} data-id="element-482">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mb-6" data-id="element-483">
                <ShieldIcon className="h-6 w-6" data-id="element-484" />
              </div>
              <h3 className="text-xl font-bold mb-4" data-id="element-485">Protection</h3>
              <p className="text-gray-300" data-id="element-486">
                We work to protect your personal reputation and financial
                standing through proper company transfer.
              </p>
            </motion.div>
            <motion.div className="bg-gray-900 p-8 rounded-lg" variants={fadeIn} data-id="element-487">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mb-6" data-id="element-488">
                <CheckIcon className="h-6 w-6" data-id="element-489" />
              </div>
              <h3 className="text-xl font-bold mb-4" data-id="element-490">Transparency</h3>
              <p className="text-gray-300" data-id="element-491">
                Clear, straightforward advice and pricing with no hidden fees or
                unexpected charges.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-white" data-id="element-492">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-493">
          <motion.div className="max-w-3xl mx-auto text-center" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-494">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-495">
              Ready to Hand Over Your Business Challenges?
            </h2>
            <p className="text-xl text-gray-600 mb-8" data-id="element-496">
              Contact us today for a confidential discussion about your
              situation.
            </p>
            <CTAGroup className="justify-center" showPriority={true} data-id="element-497" />
            <p className="text-sm text-gray-500 mt-6" data-id="element-498">
              Average response time: 24 hours | 100% confidential
            </p>
          </motion.div>
        </div>
      </section>
      {/* Website Credit */}
      <section className="py-8 bg-gray-50 border-t border-gray-200" data-id="element-499">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-500">
          <motion.div className="flex flex-col items-center justify-center" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} data-id="element-501">
            <a href="https://bright-pixxel.com" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-4 hover:opacity-80 transition-opacity" data-id="element-502">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pkCFBj5bhhYQqeVubg29ef/Geometric_Logo_of_Bright_Pixxel_Studio.png" alt="Bright Pixxel Studio" className="h-12 w-auto" data-id="element-503" />
              <span className="text-base text-gray-600" data-id="element-504">
                Created by{' '}
                <span className="font-semibold text-gray-900" data-id="element-505">
                  Bright Pixxel Studio
                </span>
              </span>
            </a>
          </motion.div>
        </div>
      </section>
    </div>;
};